import React from "react";
import { useTranslation } from "react-i18next";
import { Channel } from "../../../../../lib/api/channels";
import { ChannelsList } from "../../ChannelsList";

interface EntitySelectionViewProps {
    channel: Channel;
    selectedEntityIds: string[];
    rootEntityId?: string;
    bridgeEntityName?: string;
    isAnimating: boolean;
    onBack: () => void;
    onClose: () => void;
    onToggleEntity: (entityIds: string | string[], isSelected: boolean) => void;
    onValidate: () => void;
    boardCurrency?: string;
    highlightedEntityId?: string;
    shouldHighlight?: boolean;
    onClearHighlight?: () => void;
}

export const EntitySelectionView: React.FC<EntitySelectionViewProps> = ({
    channel,
    selectedEntityIds,
    rootEntityId,
    bridgeEntityName,
    isAnimating,
    onBack,
    onClose,
    onToggleEntity,
    onValidate,
    boardCurrency,
    highlightedEntityId,
    shouldHighlight,
    onClearHighlight,
}) => {
    const { t } = useTranslation("common");
    const [summary, setSummary] = React.useState({ count: 0, total: 0 });


    // Render channel icon
    const renderChannelIcon = () => {
        if (channel.icon && (channel.icon.startsWith("http") || channel.icon.startsWith("/"))) {
            return <img src={channel.icon} alt={channel.name} className="w-6 h-6 object-contain" />;
        }
        if (channel.channelTypeConfig?.icon && (channel.channelTypeConfig.icon.startsWith("/") || channel.channelTypeConfig.icon.startsWith("http"))) {
            return <img src={channel.channelTypeConfig.icon} alt={channel.name} className="w-6 h-6 object-contain" />;
        }
        if (channel.icon) {
            return <span className="text-xl leading-none">{channel.icon}</span>;
        }
        return (
            <span className="w-5 h-5 text-accent-primary" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_4e2f2f0b.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_4e2f2f0b.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
        );
    };

    return (
        <>
            {/* Header */}
            <div className="flex-shrink-0 px-6 py-4 border-b border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-950 z-10">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                        <button
                            onClick={onBack}
                            className="flex-shrink-0 p-1.5 rounded-lg text-neutral-400 dark:text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-primary focus-visible:ring-offset-1"
                            disabled={isAnimating}
                        >
                            <span className="w-5 h-5" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_1205ef32.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_1205ef32.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                        </button>
                        <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-white dark:bg-neutral-800 rounded-full border border-neutral-200 dark:border-neutral-700 text-xl overflow-hidden shadow-sm">
                            {renderChannelIcon()}
                        </div>
                        <div className="flex-1 min-w-0 flex flex-col justify-center">
                            <h2 className="text-base font-bold text-neutral-600 dark:text-neutral-100 leading-none mb-0.5 truncate">
                                {bridgeEntityName || channel.name}
                            </h2>
                            <p className="text-xs text-neutral-400 dark:text-neutral-500 font-medium truncate">
                                {t("itemSelection.selectEntitiesInstructions")}
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={() => onClose()}
                        className="flex-shrink-0 ml-3 p-2 hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded-lg transition-colors focus:outline-none"
                    >
                        <span className="w-5 h-5 text-neutral-500 dark:text-neutral-400" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_4a9e8065.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_4a9e8065.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                    </button>
                </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto" style={{ minHeight: 'calc(85vh - 140px)', maxHeight: 'calc(85vh - 140px)' }}>
                <ChannelsList
                    channels={React.useMemo(() => [channel], [channel])}
                    selectedEntityIds={selectedEntityIds}
                    onToggleEntity={onToggleEntity}
                    onRemoveChannel={() => { }}
                    rootEntityId={rootEntityId}
                    onSelectionSummaryChange={React.useCallback((count: number, total: number) => setSummary({ count, total }), [])}
                    highlightedEntityId={highlightedEntityId}
                    shouldHighlight={shouldHighlight}
                    onClearHighlight={onClearHighlight}
                />
            </div>

            {/* Footer */}
            <div className="flex-shrink-0 px-6 py-4 border-t border-neutral-200 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-900/50 rounded-b-2xl">
                <div className="flex justify-between items-center">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-0 sm:gap-1.5 whitespace-nowrap justify-center min-w-0">
                        <span className="text-xs text-neutral-500 dark:text-neutral-400 leading-tight">
                            {summary.count} {t("itemSelection.selected")}
                        </span>
                        <span className={`text-xs font-bold leading-tight ${summary.total > 0 ? 'text-emerald-600 dark:text-emerald-400' : summary.total < 0 ? 'text-rose-600 dark:text-rose-400' : 'text-neutral-500 dark:text-neutral-400'}`}>
                            {Math.abs(summary.total).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} {
                                boardCurrency ? (0).toLocaleString('en-US', { style: 'currency', currency: boardCurrency, minimumFractionDigits: 0, maximumFractionDigits: 0 }).replace(/[\d\s.,]/g, '') : '$'
                            }
                        </span>
                    </div>
                    <div className="flex gap-2 flex-shrink-0 whitespace-nowrap">
                        <button
                            onClick={() => onBack ? onBack() : onClose()}
                            className="px-4 py-2 text-sm font-medium text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded-lg transition-colors"
                        >
                            {t("actions.cancel")}
                        </button>
                        <button
                            onClick={onValidate}
                            className="px-4 py-2 text-sm font-medium text-white bg-accent-primary hover:bg-accent-primary-hover rounded-lg transition-colors flex items-center gap-2"
                        >
                            {t("itemSelection.validate")}
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
};
